<script setup lang="ts" generic="T extends { id: string | number; children: unknown[] }">
import type { VNodeChild } from "vue";
import { onMounted, useTemplateRef } from "vue";

import DropdownMenu from "@/components/ui/DropdownMenu/DropdownMenu.vue";
import SelectableList from "@/components/ui/SelectableList/SelectableList.vue";
import SvgIcon from "@/components/ui/SvgIcon/SvgIcon.vue";
import { useNativeEvents } from "@/composables/useNativeEvents";

import { useHoverMenu } from "./internal/useHoverMenu";

defineProps<{ header?: string; items: T[]; modelValue: T[] }>();

const emit = defineEmits<{ "update:modelValue": [value: T[]] }>();

defineSlots<{
  trigger(props: { item: T }): VNodeChild;
  submenu(props: { item: T; children: T["children"] }): VNodeChild;
}>();

const { addEventListener } = useNativeEvents();
const { activeId, handleHoverEnter, handleHoverLeave } = useHoverMenu();
const rootRef = useTemplateRef<HTMLElement>("rootRef");

// Handle selection changes from SelectableList
function handleSelectionChange(selected: T[]) {
  emit("update:modelValue", selected);
}

// Helper to extract item ID from event target
function getItemIdFromEvent(event: PointerEvent): string | null {
  const wrapper = (event.target as HTMLElement).closest(".menu-list-item .wrapper");
  return (
    wrapper?.querySelector("[data-cascade-item-id]")?.getAttribute("data-cascade-item-id") ?? null
  );
}

// Attach hover event listeners using event delegation
onMounted(() => {
  const component = rootRef.value;
  if (!component) return;

  addEventListener(
    component,
    "pointerover",
    (event) => {
      const id = getItemIdFromEvent(event);
      if (id) handleHoverEnter(id);
    },
    { capture: true, passive: true },
  );

  addEventListener(
    component,
    "pointerout",
    (event) => {
      const id = getItemIdFromEvent(event);
      if (id) handleHoverLeave(id);
    },
    { capture: true, passive: true },
  );
});
</script>

<template>
  <div ref="rootRef" class="cascading-select-list">
    <SelectableList
      :model-value
      :values="items"
      :header
      @update:model-value="handleSelectionChange"
    >
      <template #default="{ item }">
        <!-- The trigger ends 34px before the row's right edge (chevron, gap, paddings), so the
             distance clears that plus a 2px gap -->
        <DropdownMenu
          placement="right-start"
          :offset="{ left: -8, top: 36 }"
          stay-open-on-content-click
          width="sm"
          :is-open="activeId === String(item.id)"
          :data-cascade-item-id="String(item.id)"
        >
          <template #trigger>
            <div class="cascade-trigger">
              <slot name="trigger" :item />
            </div>
          </template>

          <div
            @pointerenter="handleHoverEnter(String(item.id))"
            @pointerleave="handleHoverLeave(String(item.id))"
          >
            <slot name="submenu" :item :children="item.children" />
          </div>
        </DropdownMenu>
      </template>
      <template #after>
        <SvgIcon name="arrow/chevron-right" width="16" height="16" />
      </template>
    </SelectableList>
  </div>
</template>

<style scoped>
:deep(.menu-list-item .wrapper) {
  padding: 0;
}

:deep(.menu-list-item .dropdown-menu) {
  display: block;
  padding: 6px;
}

.cascade-trigger {
  display: flex;
  gap: 16px;
  align-items: center;
  min-width: 180px;
}
</style>
